import { phone } from 'phone'
import { useEffect, useState, KeyboardEvent, JSX } from 'react'
import './TextInput.css'

export const AutoCompleteValues = [
  'on',
  'off',
  'address-line1',
  'address-line2',
  'address-line3',
  'address-level1',
  'address-level2',
  'address-level3',
  'address-level4',
  'street-address',
  'country',
  'country-name',
  'postal-code',
  'name',
  'additional-name',
  'family-name',
  'give-name',
  'honoric-prefix',
  'honoric-suffix',
  'nickname',
  'organization-title',
  'username',
  'new-password',
  'current-password',
  'bday',
  'bday-day',
  'bday-month',
  'bday-year',
  'sex',
  'one-time-code',
  'organization',
  'cc-name',
  'cc-given-name',
  'cc-additional-name',
  'cc-family-name',
  'cc-number',
  'cc-exp',
  'cc-exp-month',
  'cc-exp-year',
  'cc-csc',
  'cc-type',
  'transaction-currency',
  'transaction-amount',
  'language',
  'url',
  'email',
  'photo',
  'tel',
  'tel-country-code',
  'tel-national',
  'tel-area-code',
  'tel-local',
  'tel-local-prefix',
  'tel-local-suffix',
  'tel-extension',
  'impp',
] as const

export type AutoComplete = (typeof AutoCompleteValues)[number]

interface TextInputProps {
  type: 'text' | 'url' | 'email' | 'phone' | 'password'
  defaultValue?: string
  name?: string
  readOnly?: boolean
  placeholder?: string
  value: string
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  onChange: (props: any) => any
  onKeyPress?: () => void
  onFocus?: () => void
  onBlur?: () => void
  disabled?: boolean
  onError?: (bool: boolean) => void
  required?: boolean
  requiredError?: string
  phoneError?: string
  emailError?: string
  customError?: string
  autoComplete?: AutoComplete
  requiredErrorLabel?: string
  phoneErrorLabel?: string
  emailErrorLabel?: string
  customErrorLabel?: string
  eyeIcon?: JSX.Element
  id?: string
  inputMode?: 'text' | 'numeric'
  ariaLabel?: string
}

export const TextInput = ({
  type,
  defaultValue,
  name,
  readOnly,
  placeholder,
  value,
  onChange,
  onKeyPress,
  onFocus,
  onBlur,
  disabled,
  onError,
  required,
  requiredErrorLabel,
  phoneErrorLabel,
  emailErrorLabel,
  customErrorLabel,
  autoComplete,
  eyeIcon,
  id,
  inputMode,
  ariaLabel,
}: TextInputProps) => {
  const [errorLabel, setErrorLabel] = useState('')
  const [focused, setFocused] = useState<boolean>(false)

  function handleValidation(value: string) {
    setErrorLabel('')
    onError && onError(false)

    const reEmail = new RegExp('^[a-zA-Z0-9_.]+@[a-zA-Z0-9-]+\\.[a-zA-Z0-9-.]+$')
    // const rePhone =
    //   /^[\+]?[(]?[0-9]{3}[)]?[-\s\.]?[0-9]{3}[-\s\.]?[0-9]{4,6}$/im;

    const rePhone = phone(value)

    if (value && value.length) {
      if (type === 'email' && !reEmail.test(value)) {
        setErrorLabel(emailErrorLabel ? emailErrorLabel : 'Please enter a valid e-mail address')
        onError && onError(true)
      }

      if (type === 'phone' && !rePhone.isValid) {
        setErrorLabel(
          phoneErrorLabel
            ? phoneErrorLabel
            : 'Please enter a valid phone number with country code prefix'
        )
        onError && onError(true)
      }

      if (type === 'text' && customErrorLabel) {
        setErrorLabel(customErrorLabel)
        onError && onError(true)
      }
    } else {
      if (required && focused) {
        setErrorLabel(requiredErrorLabel ? requiredErrorLabel : 'Please fill the required field')
        onError && onError(true)
      }
    }
  }

  useEffect(() => {
    handleOnChange(value)
  }, [value])

  function handleOnChange(value: string) {
    handleValidation(value)
    onChange && onChange(value)
  }

  function handleOnKeyPress(event: KeyboardEvent<HTMLInputElement>) {
    if (event.key === 'Enter') {
      onKeyPress && onKeyPress()
    }
  }

  function handleOnFocus() {
    setFocused(true)
    onFocus && onFocus()
  }

  function handleOnBlur(value: string) {
    handleValidation(value)
    onBlur && onBlur()
  }

  return (
    <div className="text-input__container">
      <input
        className="text-input__input onboarding-text-input"
        type={type}
        id={id}
        inputMode={inputMode}
        aria-label={ariaLabel}
        defaultValue={defaultValue}
        name={name}
        required={!!required}
        readOnly={!!readOnly}
        placeholder={placeholder}
        value={value}
        onFocus={handleOnFocus}
        onChange={(e) => handleOnChange(e.target.value)}
        onKeyPress={(e) => handleOnKeyPress(e)}
        disabled={disabled}
        onBlur={(e) => handleOnBlur(e.target.value)}
        autoComplete={autoComplete || 'off'}
      />
      {eyeIcon && eyeIcon}
      <span className="text-input__error-msg onboarding-text-input-error">{errorLabel}</span>
    </div>
  )
}
